import React, { useState, useEffect } from 'react';
import { TextField, Button, FormHelperText, Grid, Typography } from '@material-ui/core';
import CollapsibleTable from './components/CollapsibleTable';
import {
  createAnExcelSheet,
  downloadAnCsv,
  getToolsUsageCreditsNeeded,
  handleGenerateOutput,
  PAID_TOOLS_PROMPT_FOR,
  resetCaptcha,
} from '../../utility/common';
import NoImageFound from '../../../public/Spinner-Logo.gif';
import { fetchKeywordDfsData } from '../../utility/cr-common';
import { AxiosError } from 'axios';
import ReCAPTCHA from 'react-google-recaptcha';
import { CONTINUE_USING_TEXT } from './components/ToolsStyleWrapper';
import {
  COMPONENT_NAMES,
  SCREEN_NAMES,
  CONTENT_AI_TOOL_NAME,
  CONTENT_AI_TOOL_CATEGORY,
  trackAnalyticsEvent,
} from '../../../src/constants/analyticsMeta';
import Cookies from 'js-cookie';
import { SkipCaptcha } from './components/SkipCaptcha';
import { useSession } from '../../utility/services/customHooks';
import { SUBSCRIPTION_STATUS } from '../../constants/common';
import CreditsInfo from './components/CreditsInfo';

export const GetKeywordStatistics = ({
  classes,
  setShowSignUpPage,
  user,
  handleResetUsage,
  isQwaDashboard = false,
  isCreditsShown = false,
  creditsRemaining = 0,
  usagePerDay = 0,
  hasValidSubscription,
  enableLockedOption,
  setShowBuyOnePass,
  setIsRedirectToWebflow,
  setShowMinimumCreditsNeededMsg = (a) => {},
  setShowVerifyEmailPopup,
  freeToolsUsage = 0,
  showFreeToolsUsage = false,
  isCorpEmail = false,
}) => {
  const [input, setInput] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({});
  const qttr_tools_auth = Cookies.get('qttr_tools_auth');
  const qttr_tools_daypass_auth = Cookies.get('qttr_tools_daypass_auth');
  const [recaptchaValue, setRecaptchaValue] = useState(null);
  const [captchaError, setCaptchaError] = useState('');
  const [creditsNeeded, setCreditsNeeded] = useState(1);
  const session = useSession();

  const handleRecaptchaChange = (value) => {
    setRecaptchaValue(value);
    setCaptchaError('');
  };

  useEffect(() => {
    trackAnalyticsEvent({
      isAnonymousUser: !user,
      payload: {
        label: 'Visited Free Tool',
        component_name: COMPONENT_NAMES.contentai_ai_tools,
        screen_name: SCREEN_NAMES.contentai_ai_tools,
        tool_name: CONTENT_AI_TOOL_NAME.keyword_statistics,
        tool_category: CONTENT_AI_TOOL_CATEGORY.keyword_research,
        isQwaDashboard: isQwaDashboard,
      },
    });
  }, []);

  const handleClusterKeywords = async () => {
    setLoading(true);
    try {
      // fetch data from dfs
      const keywords = (input?.split(',') || []).map((i) => i.trim()).filter((i) => Boolean(i));
      if (!keywords.length) {
        setError('input');
        return;
      }
      if (!(recaptchaValue || qttr_tools_auth || user)) {
        setCaptchaError('recaptchaValue');
        return;
      }
      setCaptchaError('');
      setError('');
      const payload = {
        keywords: keywords,
        include_serp_info: true,
        language_name: 'English',
        location_code: '2840',
        language_code: 'en',
        reCAPTCHAToken: recaptchaValue,
      };

      const res = await fetchKeywordDfsData(payload, true);
      if (res) {
        trackAnalyticsEvent({
          eventFor: 'button',
          buttonText: '',
          counterName: `${CONTENT_AI_TOOL_CATEGORY.keyword_research} Outputs`,
          isAddCount: true,
          user,
          isAnonymousUser: !user,
          payload: {
            label: 'Generate Output',
            component_name: COMPONENT_NAMES.contentai_ai_tools,
            screen_name: SCREEN_NAMES.contentai_ai_tools,
            tool_name: CONTENT_AI_TOOL_NAME.keyword_statistics,
            tool_category: CONTENT_AI_TOOL_CATEGORY.keyword_research,
            special_instructions_yn: input ? 'Yes' : 'No',
            isQwaDashboard: isQwaDashboard,
            creditsConsumed: creditsNeeded ? creditsNeeded : 1,
          },
        });
        if (handleResetUsage) {
          handleResetUsage();
        }
      }
      const mergedArray = keywords?.map((obj1) => {
        const obj2 = res?.data.find((obj) => obj.keyword.toUpperCase() === obj1.toUpperCase());
        return {
          keyword: obj1,
          'search volume': obj2 ? obj2.search_volume : null,
          'average CPC': obj2 ? obj2.averageCPC : null,
          'keyword difficulty': obj2 ? obj2.keyword_difficulty : null,
        };
      });
      mergedArray.sort((a, b) => b['search volume'] - a['search volume']);
      setData(mergedArray);
    } catch (error) {
      const axiosError = error as AxiosError;
      if (axiosError?.response) {
        if (axiosError.response.status === 403) {
          setShowSignUpPage(CONTINUE_USING_TEXT, {
            tool_name: CONTENT_AI_TOOL_NAME.keyword_statistics,
            tool_category: CONTENT_AI_TOOL_CATEGORY.keyword_research,
          });
          trackAnalyticsEvent({
            eventFor: 'button',
            isAnonymousUser: !user,
            payload: {
              label: 'Reached your maximum usage limit',
              component_name: COMPONENT_NAMES.contentai_ai_tools,
              screen_name: SCREEN_NAMES.contentai_ai_tools,
              tool_name: CONTENT_AI_TOOL_NAME.keyword_statistics,
              tool_category: CONTENT_AI_TOOL_CATEGORY.keyword_research,
              isQwaDashboard: isQwaDashboard,
            },
          });
        }
      }
      console.error('handleClusterKeywords:: ', error);
    } finally {
      setRecaptchaValue(null);
      resetCaptcha();
      setLoading(false);
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    let keywords = value.split(',');
    setError('');

    if (keywords.length > 50 && !hasValidSubscription && !enableLockedOption) {
      keywords = keywords.slice(0, 50); // Keep only the first 50 keywords
      setError('Please sign up to process more than 50 keywords.');
      setInput(keywords.join(','));
    } else {
      setInput(value);
    }
  };

  const handleDownload = () => {
    const { ExcelJSWorkbook, worksheet } = createAnExcelSheet('Discover Keywords');
    // define columns for the worksheet
    worksheet.columns = [
      { header: 'Keyword', key: 'keyword', width: 20 },
      { header: 'Search Volume', key: 'search_volume', width: 15 },
      { header: 'Average CPC', key: 'avg_cpc', width: 15 },
      { header: 'Keyword Difficulty', key: 'keyword_difficulty', width: 15 },
    ];

    if (Array.isArray(data)) {
      data.forEach((keywordObj) => {
        worksheet.addRow({
          keyword: keywordObj['keyword'],
          search_volume: keywordObj['search volume'],
          avg_cpc: keywordObj['average CPC'],
          keyword_difficulty: keywordObj['keyword difficulty'],
        });
      });
    }
    trackAnalyticsEvent({
      eventFor: 'button',
      buttonText: '',
      counterName: 'Outputs Accepted',
      isAddCount: true,
      user,
      isAnonymousUser: !user,
      payload: {
        label: 'Download Generated Output',
        component_name: COMPONENT_NAMES.contentai_ai_tools,
        screen_name: SCREEN_NAMES.contentai_ai_tools,
        tool_name: CONTENT_AI_TOOL_NAME.keyword_statistics,
        tool_category: CONTENT_AI_TOOL_CATEGORY.keyword_research,
        special_instructions_yn: input ? 'Yes' : 'No',
        isQwaDashboard: isQwaDashboard,
      },
    });
    downloadAnCsv(ExcelJSWorkbook, 'quattr_generated_keyword_clusters');
  };

  useEffect(() => {
    const promptFor = PAID_TOOLS_PROMPT_FOR.KEYWORD_STATISTICS;
    setCreditsNeeded(getToolsUsageCreditsNeeded(promptFor, {}));
  }, [user]);

  return (
    <div className={classes.container}>
      {loading && (
        <>
          <div className={classes.overlay}></div>
          <div className={classes.loader}>
            <img src={NoImageFound.src} alt="Loading..." />
          </div>
        </>
      )}
      {data && Object.keys(data).length ? (
        <>
          <CollapsibleTable
            isSingleLevel
            classes={classes}
            data={{ clusters: data }}
            keyName="keyword"
            columns={[
              { name: 'Keyword' },
              { name: 'Avg. Search Volume', width: '95px' },
              { name: 'Avg. CPC', width: '95px' },
              { name: 'Keyword Difficulty', width: '81px' },
            ]}
          />
          <div className={classes.buttonGroup} style={{ marginTop: '16px' }}>
            <Button
              className={`${classes.toolsYellowCTA} ${classes.toolsOutlinedCTA} ${classes.button}`}
              disabled={loading}
              style={{ float: 'right' }}
              onClick={() => {
                setData({});
                trackAnalyticsEvent({
                  eventName: 'Clicked on Go back',
                  payload: {
                    label: 'Clicked on Go back',
                  },
                });
              }}
            >
              Go back
            </Button>
            <Button
              className={`${classes.toolsYellowCTA} ${classes.button}`}
              disabled={loading}
              style={{ float: 'right' }}
              onClick={handleDownload}
            >
              Download
            </Button>
          </div>
        </>
      ) : (
        <div>
          <TextField
            multiline
            // fullWidth
            value={input}
            className={classes.textAreaInput}
            onChange={handleInputChange}
            maxRows={5}
            InputProps={{
              style: { backgroundColor: '#fff', borderRadius: '4px !important', padding: '16px' },
              disableUnderline: true,
            }}
            error={!!error}
            minRows={5}
            helperText={error.includes('input') ? '* Required' : error.includes('Please sign up') ? error : ''}
            rows={5}
            variant="filled"
            placeholder="Enter list of keywords separated by ,"
          />
          {!qttr_tools_auth && !user && (
            <>
              <ReCAPTCHA
                className={classes.recaptcha}
                sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}
                onChange={handleRecaptchaChange}
              />
              {captchaError?.includes('recaptchaValue') && (
                <FormHelperText className="red">Please complete the reCAPTCHA to verify you're human.</FormHelperText>
              )}
              <SkipCaptcha
                classes={classes}
                meta={{
                  component_name: COMPONENT_NAMES.contentai_ai_tools,
                  screen_name: SCREEN_NAMES.contentai_ai_tools,
                  tool_name: CONTENT_AI_TOOL_NAME.keyword_statistics,
                  tool_category: CONTENT_AI_TOOL_CATEGORY.keyword_research,
                }}
                setShowVerifyEmailPopup={setShowVerifyEmailPopup}
              />
            </>
          )}
          <div className={`${isCreditsShown ? classes.creditsShown : classes.freeCreditsShown}`}>
            {isCreditsShown ? (
              <CreditsInfo
                classes={classes}
                creditsRemaining={creditsRemaining}
                creditsNeeded={creditsNeeded}
                usagePerDay={usagePerDay}
              />
            ) : !isQwaDashboard || showFreeToolsUsage ? (
              <CreditsInfo
                classes={classes}
                creditsRemaining={freeToolsUsage}
                creditsNeeded={creditsNeeded}
                usagePerDay={isCorpEmail ? 10 : 7}
                showFreeToolsUsage={showFreeToolsUsage}
              />
            ) : null}
            <div className={`d-flex ${classes.buttonGroup}`} style={{ marginTop: '16px' }}>
              {!hasValidSubscription && !enableLockedOption ? (
                <p style={{ marginRight: '16px' }}>{input.split(',').filter((i) => Boolean(i)).length}/50</p>
              ) : null}
              <Button
                className={classes.toolsYellowCTA}
                disabled={loading}
                style={{ float: 'right' }}
                onClick={() =>
                  handleGenerateOutput(
                    creditsRemaining,
                    creditsNeeded,
                    isQwaDashboard,
                    session,
                    qttr_tools_daypass_auth,
                    setShowMinimumCreditsNeededMsg,
                    setShowBuyOnePass,
                    handleClusterKeywords,
                    trackAnalyticsEvent,
                    freeToolsUsage,
                    showFreeToolsUsage
                  )
                }
              >
                Get Keyword Statistics
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
